import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, CalendarClock, CheckCircle2, Clock } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import type { Deal } from '@/types'
import { formatDateTime } from '@/utils/format-date'
import { InfoField } from './InfoField'
import { QualificacaoSection } from './QualificacaoSection'
import { getMeetingTiming, toDatetimeLocalValue } from './reuniaoMeetingUtils'
import { ReuniaoNextStepModal } from './ReuniaoNextStepModal'
import { useReuniaoAgendadaActions } from './useReuniaoAgendadaActions'
import type { ReuniaoFooterAction } from './useDealDetailPanel'

export const REUNIAO_AGENDADA_SECTION_ID = 'reuniao-agendada-section'
export const REUNIAO_BANT_SECTION_ID = 'reuniao-bant-section'

export function ReuniaoAgendadaSection({
  deal,
  onDealPatched,
  highlightBant = false,
  onRegisterConfirm,
}: {
  deal: Deal
  onDealPatched: (updates: Partial<Deal>) => void
  highlightBant?: boolean
  onRegisterConfirm?: (action: ReuniaoFooterAction | null) => void
}) {
  const bantSectionRef = useRef<HTMLDivElement>(null)
  const reuniao = deal.reunioes?.[0]
  const meetingDate = reuniao?.data ?? deal.dataReuniao
  const timing = useMemo(() => getMeetingTiming(meetingDate), [meetingDate])

  const [showReschedule, setShowReschedule] = useState(false)
  const [rescheduleAt, setRescheduleAt] = useState(toDatetimeLocalValue(meetingDate))

  const actions = useReuniaoAgendadaActions(deal, reuniao, onDealPatched)

  useEffect(() => {
    if (!highlightBant) return
    const section = bantSectionRef.current
    if (!section) return
    const frame = requestAnimationFrame(() => {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
    return () => cancelAnimationFrame(frame)
  }, [deal.id, highlightBant])

  useEffect(() => {
    onRegisterConfirm?.({
      confirm: actions.handleConfirmarClick,
      disabled: !meetingDate,
      confirming: actions.confirming,
    })
    return () => onRegisterConfirm?.(null)
  }, [actions.confirming, actions.handleConfirmarClick, meetingDate, onRegisterConfirm])

  return (
    <>
      <div
        id={REUNIAO_AGENDADA_SECTION_ID}
        className="deal-detail-hero-card glass-card scroll-mt-4 space-y-4 p-4"
      >
        <h3 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-2">
          <CalendarClock size={14} />
          Reunião Agendada
        </h3>

        {!meetingDate && (
          <div className="flex items-start gap-2 rounded-md bg-amber-400/5 border border-amber-400/10 p-3">
            <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-text-label">
              Este deal está em Reunião Agendada, mas não há data registrada. Reagende abaixo ou volte
              para SQL.
            </p>
          </div>
        )}

        {meetingDate && (
          <div className="rounded-md bg-white/[0.03] border border-white/[0.06] p-3 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-text-primary">{formatDateTime(meetingDate)}</p>
                {timing && (
                  <p className="text-xs text-text-label mt-1 flex items-center gap-1">
                    <Clock size={12} />
                    {timing.label}
                  </p>
                )}
              </div>
              <Badge variant="info">{reuniao?.status ?? 'Agendada'}</Badge>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <InfoField label="Tipo" value={reuniao?.tipo ?? 'Descoberta'} />
              <InfoField label="Closer" value={reuniao?.closerNome ?? deal.closerNome ?? '—'} />
            </div>

            {timing?.tone === 'past' && (
              <div className="flex items-start gap-2 rounded-md bg-violet-400/5 border border-violet-400/10 p-3">
                <CheckCircle2 size={14} className="text-violet-300 shrink-0 mt-0.5" />
                <p className="text-xs text-text-label">
                  A data já passou. Confirme pelo botão amarelo no rodapé.
                </p>
              </div>
            )}
          </div>
        )}

        <div
          id={REUNIAO_BANT_SECTION_ID}
          ref={bantSectionRef}
          className="scroll-mt-4 space-y-3 border-t border-white/[0.06] pt-2"
        >
          <h4 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent">
            <CheckCircle2 size={14} />
            Observação
          </h4>
          <p className="text-xs text-text-label">
            O campo em amarelo é obrigatório para confirmar a reunião e avançar.
          </p>
          <QualificacaoSection
            deal={actions.dealWithBant}
            onDealPatched={actions.handleBantPatch}
            required
            embedded
            deferSave
          />
        </div>

        <button
          type="button"
          onClick={() => setShowReschedule((value) => !value)}
          className="text-xs text-accent hover:underline"
        >
          {showReschedule ? 'Cancelar reagendamento' : 'Reagendar reunião'}
        </button>

        {showReschedule && (
          <div className="space-y-2 pt-2 border-t border-white/[0.06]">
            <input
              type="datetime-local"
              value={rescheduleAt}
              onChange={(e) => setRescheduleAt(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-sm rounded-md px-3 py-2 outline-none focus:border-accent"
            />
            <Button
              variant="secondary"
              size="sm"
              loading={actions.rescheduling}
              onClick={() => actions.handleReagendar(rescheduleAt, () => setShowReschedule(false))}
              className="w-full"
            >
              Salvar nova data
            </Button>
          </div>
        )}
      </div>

      <ReuniaoNextStepModal
        open={actions.showNextStepModal}
        confirming={actions.confirming}
        onClose={() => actions.setShowNextStepModal(false)}
        onGoToProposta={() => actions.finalizeConfirmacao(true)}
        onStayInRealizada={() => actions.finalizeConfirmacao(false)}
      />
    </>
  )
}
